// Dashboard V2 preview override.
//
// The authoritative control for the new dashboard is the PER-COMPANY setting
// (Company → Settings, super admin). This module only provides a *preview*
// override for a super admin / developer, with these rules:
//
//   • It is LIVE-ONLY: taken from the current URL (?dashboard=v2 / ?dashboard=v1)
//     and kept in sessionStorage so it survives SPA navigation within the tab.
//   • It is NEVER persisted to localStorage, so it can't silently "stick" and
//     override the company setting across sessions.
//   • A stale value from the old persistent key is actively cleared, so browsers
//     that previewed V2 before this change fall back to the company setting.
//
// getDashboardV2Preview() returns: true (force V2), false (force old), or
// null (no preview opinion → use the company setting).

const QUERY_PARAM = "dashboard"; // ?dashboard=v2 enables, ?dashboard=v1 disables
const SESSION_KEY = "feature_dashboard_v2_preview";
const LEGACY_STORAGE_KEY = "feature_dashboard_v2"; // old persistent key, now ignored

const ON_VALUES = new Set(["v2", "true", "1", "on", "yes"]);
const OFF_VALUES = new Set(["v1", "false", "0", "off", "no"]);

const safeSession = {
  get: (k) => {
    try {
      return window.sessionStorage.getItem(k);
    } catch (_) {
      return null;
    }
  },
  set: (k, v) => {
    try {
      window.sessionStorage.setItem(k, v);
    } catch (_) {
      /* storage unavailable - ignore */
    }
  },
  remove: (k) => {
    try {
      window.sessionStorage.removeItem(k);
    } catch (_) {
      /* ignore */
    }
  },
};

// Remove the obsolete persistent override so it can never force V2 again.
const clearLegacyPersistentOverride = () => {
  try {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch (_) {
    /* ignore */
  }
};

/**
 * @returns {boolean|null} true = force V2, false = force old, null = defer to
 * the per-company setting.
 */
export const getDashboardV2Preview = () => {
  if (typeof window === "undefined") return null;
  clearLegacyPersistentOverride();

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has(QUERY_PARAM)) {
      const raw = (params.get(QUERY_PARAM) || "").toLowerCase();
      let enabled = null;
      if (ON_VALUES.has(raw)) enabled = true;
      else if (OFF_VALUES.has(raw)) enabled = false;
      if (enabled !== null) {
        safeSession.set(SESSION_KEY, enabled ? "true" : "false");
        return enabled;
      }
    }
  } catch (_) {
    /* fall through to session value */
  }

  const stored = safeSession.get(SESSION_KEY);
  if (stored === "true") return true;
  if (stored === "false") return false;
  return null;
};

/** Set the session-scoped preview (used by the super-admin System Settings). */
export const setDashboardV2Preview = (enabled) =>
  safeSession.set(SESSION_KEY, enabled ? "true" : "false");

/** Clear the session preview (and any stale persistent key). */
export const clearDashboardV2Preview = () => {
  safeSession.remove(SESSION_KEY);
  clearLegacyPersistentOverride();
};
